import React, { useState } from 'react';
import { Activity, ChevronDown, ExternalLink, ShieldCheck, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Card, Button, StatusPill, SystemBadge } from '../components/ui/Primitives';
export function Connections() {
  return <div className="max-w-6xl mx-auto" data-id="element-137">
      <div className="mb-6 flex items-center justify-between" data-id="element-138">
        <div data-id="element-139">
          <h1 className="text-xl font-semibold text-slate-900" data-id="element-140">
            System Connections
          </h1>
          <p className="text-sm text-slate-500" data-id="element-141">
            Diagnostics and health for connected platforms.
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-142">
          <Activity className="w-4 h-4 text-amber-600" data-id="element-143" />
          <span className="text-sm font-medium text-amber-800" data-id="element-144">
            3 of 4 systems healthy • GHL experiencing elevated latency
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6" data-id="element-145">
        <ConnectionCard system="bonzo" status="Healthy" latency="124ms" rateLimit="45/100" data-id="element-146" />
        <ConnectionCard system="ghl" status="Degraded" latency="842ms" rateLimit="98/100" data-id="element-147" />
        <ConnectionCard system="five9" status="Healthy" latency="86ms" rateLimit="12/50" data-id="element-148" />
        <ConnectionCard system="salesforce" status="Healthy" latency="210ms" rateLimit="N/A" data-id="element-149" />
      </div>
    </div>;
}
function ConnectionCard({
  system,
  status,
  latency,
  rateLimit
}: any) {
  const isHealthy = status === 'Healthy';
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle');
  const [expanded, setExpanded] = useState(false);
  const handleTest = () => {
    setTestStatus('testing');
    setTimeout(() => {
      setTestStatus(isHealthy ? 'ok' : 'fail');
      setTimeout(() => setTestStatus('idle'), 4000);
    }, 1500);
  };
  return <Card className="flex flex-col" data-id="element-150">
      <div className="p-5 border-b border-slate-100" data-id="element-151">
        <div className="flex items-start justify-between mb-4" data-id="element-152">
          <div className="flex items-center gap-3" data-id="element-153">
            <SystemBadge system={system} showLabel={false} className="scale-150 origin-left" data-id="element-154" />
            <div data-id="element-155">
              <h2 className="text-lg font-semibold text-slate-900 capitalize" data-id="element-156">
                {system} API
              </h2>
              <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-1" data-id="element-157">
                <ShieldCheck className="w-3 h-3 text-emerald-500" data-id="element-158" /> Token valid
                (expires in 14d)
              </div>
            </div>
          </div>
          <StatusPill status={isHealthy ? 'success' : 'warning'} dot pulse={isHealthy} data-id="element-159" />
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-160">
          <div data-id="element-161">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-162">
              Avg Latency
            </div>
            <div className={`text-lg font-mono font-medium ${isHealthy ? 'text-slate-700' : 'text-amber-600'}`} data-id="element-163">
              {latency}
            </div>
          </div>
          <div data-id="element-164">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-165">
              Rate Limit (/min)
            </div>
            <div className="text-lg font-mono font-medium text-slate-700" data-id="element-166">
              {rateLimit}
            </div>
          </div>
          <div data-id="element-167">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-168">
              Last Sync
            </div>
            <div className="text-sm font-medium text-slate-700 mt-1" data-id="element-169">
              2 mins ago
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-170">
          <div className="text-xs font-mono text-slate-400 truncate max-w-[200px]" data-id="element-171">
            https://api.{system}.com/v3/...
          </div>
          <div className="flex items-center gap-3" data-id="element-172">
            {testStatus === 'testing' && <Loader2 className="w-4 h-4 text-electric-500 animate-spin" data-id="element-173" />}
            {testStatus === 'ok' && <span className="text-xs font-medium text-emerald-600 flex items-center" data-id="element-174">
                <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-175" /> Success — 124ms
              </span>}
            {testStatus === 'fail' && <span className="text-xs font-medium text-red-600 flex items-center" data-id="element-176">
                <XCircle className="w-3 h-3 mr-1" data-id="element-177" /> Timeout
              </span>}
            <Button variant="outline" size="sm" onClick={handleTest} disabled={testStatus === 'testing'} data-id="element-178">
              Test Connection
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-slate-50 px-5 py-3 flex items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors text-xs font-medium text-slate-500" onClick={() => setExpanded(!expanded)} data-id="element-179">
        {expanded ? 'Hide Diagnostics' : 'View Diagnostics'}
        <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-180" />
      </div>
      {expanded && <div className="p-5 bg-slate-50 border-t border-slate-200 text-sm animate-in slide-in-from-top-2 fade-in" data-id="element-181">
          <div className="space-y-4" data-id="element-182">
            <div data-id="element-183">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-184">
                Endpoint URL
              </div>
              <div className="font-mono text-xs text-slate-700 bg-white p-2 rounded border border-slate-200" data-id="element-185">
                https://api.{system}.com/v3/contacts/sync
              </div>
            </div>
            <div data-id="element-186">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-187">
                Auth Headers
              </div>
              <div className="font-mono text-xs text-slate-700 bg-white p-2 rounded border border-slate-200" data-id="element-188">
                <div data-id="element-189">Authorization: Bearer sk_live_••••••••••••a8f2</div>
                <div data-id="element-190">X-Integration-Version: 2.1.0</div>
              </div>
            </div>
            <div data-id="element-191">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-192">
                Recent Calls
              </div>
              <div className="bg-white rounded border border-slate-200 overflow-hidden" data-id="element-193">
                <table className="w-full text-left text-xs" data-id="element-194">
                  <thead className="bg-slate-50 border-b border-slate-200" data-id="element-195">
                    <tr data-id="element-196">
                      <th className="px-3 py-2 font-medium text-slate-500" data-id="element-197">
                        Time
                      </th>
                      <th className="px-3 py-2 font-medium text-slate-500" data-id="element-198">
                        Method
                      </th>
                      <th className="px-3 py-2 font-medium text-slate-500" data-id="element-199">
                        Status
                      </th>
                      <th className="px-3 py-2 font-medium text-slate-500" data-id="element-200">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono" data-id="element-201">
                    {[1, 2, 3].map(i => <tr key={i} data-id="element-202">
                        <td className="px-3 py-2 text-slate-500" data-id="element-203">{i}m ago</td>
                        <td className="px-3 py-2 text-electric-600" data-id="element-204">POST</td>
                        <td className="px-3 py-2 text-emerald-600" data-id="element-205">200 OK</td>
                        <td className="px-3 py-2 text-slate-500" data-id="element-206">
                          {80 + i * 12}ms
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>}
    </Card>;
}